import {
  CustomButtonActionHandler,
  CustomButtonActionHandlerContext
} from '@shared/components/custom-action-button/handlers/custom-button-action-handler';
import { CustomButtonActionType } from '@shared/components/custom-action-button/handlers/custom-button-action-handler-registry';
import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { DialogService } from 'primeng/dynamicdialog';
import * as _ from 'lodash';
import { LodashTemplateEvaluator } from '@shared/components/smart-table/LodashTemplateEvaluator';
import { Utils as FormioUtils } from '@evi-ui/js';

/**
 * Handles CustomButtonActionType.refreshCurrentPage.
 *
 * Angular's router treats navigating to the same URL as a no-op
 * (`onSameUrlNavigation` is 'ignore' app-wide), so a plain
 * `router.navigate([router.url])` would not re-run route resolvers/guards
 * or recreate the routed component. To force an in-app refresh, this
 * processor navigates to a neutral route with `skipLocationChange: true`
 * (no visible URL/history change) and then navigates back to the current
 * URL, which the router treats as a fresh navigation.
 *
 * Because the router state (`history.state.state`) is not preserved across
 * that hop automatically, `resolveStateToPropagate` re-applies it using the
 * same `navigationConfig.stateToPropagate` / `stateToPropagateEvaluator`
 * config as NavigateToUrlActionProcessor, falling back to whatever state is
 * already present so a plain refresh doesn't silently drop context (e.g.
 * an id) the page relies on.
 */
@Injectable({ providedIn: 'root' })
export class RefreshPageActionProcessor implements CustomButtonActionHandler {
  constructor(
    private readonly router: Router,
    private readonly dialogService: DialogService
  ) {}

  isApplicable(actionType: CustomButtonActionType): boolean {
    return actionType === CustomButtonActionType.refreshCurrentPage;
  }

  performAction(context: CustomButtonActionHandlerContext): Promise<any> {
    this.dialogService.dialogComponentRefMap?.forEach((dialog) => {
      dialog?.destroy();
    });

    const currentUrl = this.router.url;
    const stateToPropagate = this.resolveStateToPropagate(context);

    return this.router
      .navigateByUrl('/', { skipLocationChange: true })
      .then(() =>
        this.router.navigateByUrl(currentUrl, {
          state: { state: stateToPropagate }
        })
      );
  }

  private resolveStateToPropagate(context: CustomButtonActionHandlerContext) {
    const navigationConfig = context.actionProcessor.navigationConfig;
    const formInstance = context.formInstance;

    if (navigationConfig?.stateToPropagate) {
      return _.get(
        formInstance.submission.data,
        navigationConfig.stateToPropagate
      );
    }

    if (navigationConfig?.stateToPropagateEvaluator) {
      let stateToPropagate = LodashTemplateEvaluator.interpolate(
        navigationConfig.stateToPropagateEvaluator,
        formInstance.submission
      );
      return FormioUtils.evaluate(
        stateToPropagate,
        formInstance.submission,
        'value',
        true
      );
    }

    return globalThis.history.state?.state;
  }
}
